/**
 * Header bell with unread badge and a small unread-list popup.
 * The menu is portaled to document.body so Layout overflow-hidden cannot clip it.
 * Opening does not mark items read; clicking a row does.
 */
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as FiIcons from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { InboxItem, useNotifications } from '../../context/NotificationsContext';

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

const NotificationBell: React.FC = () => {
  const { unreadCount, items, fetchInbox, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);

    let timer: number | undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointer);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const unreadItems = items.filter((item) => !item.readAt).slice(0, 10);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      void fetchInbox({ unreadOnly: true });
    }
  };

  const handleItemClick = (item: InboxItem) => {
    void markRead([item.id]);
    setOpen(false);
  };

  const menu =
    open &&
    createPortal(
      <div
        ref={panelRef}
        role="menu"
        style={{ top: coords.top, right: coords.right }}
        className="fixed z-[200] w-[min(22rem,calc(100vw-2rem))] origin-top-right rounded-2xl border border-[#e4d4c4] bg-[#fbf6ee] shadow-[0_18px_40px_-18px_rgba(90,40,30,0.45)] overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#eadccb] bg-[#f3eadc]">
          <p className="text-sm font-semibold tracking-tight text-[#4a2c24]">
            New notifications
          </p>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            View all
          </Link>
        </div>

        {unreadItems.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-monday-text-muted">
            No new notifications.
          </p>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-[#eadccb]">
            {unreadItems.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onSelect={() => handleItemClick(item)}
              />
            ))}
          </ul>
        )}
      </div>,
      document.body
    );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="relative h-10 w-10 inline-flex items-center justify-center rounded-xl text-monday-text-muted hover:text-monday-text hover:bg-monday-header focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-150"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {FiIcons.FiBell({ size: 20 })}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-error-500 text-white text-[10px] font-bold leading-[18px] text-center ring-2 ring-white shadow-sm">
            {badgeLabel}
          </span>
        )}
      </button>
      {menu}
    </div>
  );
};

const NotificationRow: React.FC<{
  item: InboxItem;
  onSelect: () => void;
}> = ({ item, onSelect }) => {
  const navigate = useNavigate();

  const inner = (
    <div className="px-4 py-3 text-left transition-colors bg-[#f7ebe0] hover:bg-[#f3e0d0]">
      <div className="flex gap-2">
        <span className="mt-1 h-2 w-2 rounded-full bg-error-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#3b241c] truncate">{item.title}</p>
          {item.body && (
            <p className="mt-0.5 text-xs text-monday-text-muted line-clamp-2">{item.body}</p>
          )}
          <p className="mt-1 text-[11px] text-[#9a7d6c]">{relativeTime(item.createdAt)}</p>
        </div>
      </div>
    </div>
  );

  const handleClick = () => {
    onSelect();
    if (item.link) {
      navigate(item.link);
    }
  };

  return (
    <li>
      <button type="button" role="menuitem" onClick={handleClick} className="block w-full text-left">
        {inner}
      </button>
    </li>
  );
};

export default NotificationBell;
